import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus } from "lucide-react";

const FAQS = [
  {
    q: "How does pricing actually work?",
    a: "You buy credits. Every generation debits credits from your wallet based on the feature — e.g. ≈ 16 credits for a 10s standard video, 36 for cinema, 3 for an image, 4 for a 1-minute voiceover. Failed renders auto-refund. Top up anytime from $5 (100 credits) up to $100 (2,400 credits, includes 400 bonus). No subscription required to use the platform.",
  },
  {
    q: "Should I subscribe or just buy credit packs?",
    a: "Both work. PAYG: buy a $5 or $10 pack and use the platform for exactly what you need, no commitment. Subscribe: Weekly $15 (340 cr), Starter $29.99 (720 cr), Pro $59.99 (1,500 cr), Premium $99.99 (2,700 cr + ElevenLabs Cinema voiceover + Sora 2 Pro). Subscriptions deliver more credits per dollar and unlock priority queue + brand kit + commercial license.",
  },
  {
    q: "Can I cancel anytime?",
    a: "Yes. Cancel from your dashboard's pricing page in two clicks. Your plan stays active until the end of your current period, and any unused credits remain in your wallet — they don't expire.",
  },
  {
    q: "Who owns the videos I create?",
    a: "You do. Every plan includes a commercial-use license for the videos you generate. Pro and Premium tiers extend that to client work and broader sublicensing.",
  },
  {
    q: "What aspect ratios and lengths can I export?",
    a: "9:16 vertical, 1:1 square and 16:9 widescreen are supported on every plan. Single-shot clips run 4, 8 or 12 seconds (Sora 2's native lengths). For 30s or 60s commercials, use the Multi-Shot Commercial Generator — it auto-splits into 3-5 shots and stitches into one seamless ad.",
  },
  {
    q: "How do you make on-screen text so clean?",
    a: "We layer a dedicated English-text overlay engine on top of every generated video. Captions, brand names and product labels render correctly — no garbled glyphs, no foreign characters, no broken words.",
  },
  {
    q: "Is there a free trial?",
    a: "Yes. New accounts get a small starter credit grant on signup — enough to test image, voiceover and a short video — and downloads are watermarked until you top up or subscribe. From there, pay for what you actually create. No 14-day countdown.",
  },
];

export default function FAQ() {
  const [open, setOpen] = useState(0);

  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="relative bg-[#080808] py-24 md:py-32 border-t border-white/10"
    >
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <div className="mb-14 md:mb-20">
          <p className="text-xs font-bold uppercase tracking-[0.28em] text-[#D4AF37] mb-5">
            Frequently Asked
          </p>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tighter text-[#F4F4F0] leading-[1.02]">
            Answers, no asterisks.
          </h2>
        </div>

        <ul className="divide-y divide-white/10 border-t border-b border-white/10">
          {FAQS.map((item, i) => {
            const isOpen = open === i;
            return (
              <li key={i} data-testid={`faq-item-${i}`}>
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  data-testid={`faq-toggle-${i}`}
                  className="w-full flex items-center justify-between gap-6 py-6 md:py-8 text-left group"
                >
                  <span className="font-display text-lg md:text-2xl font-bold text-[#F4F4F0] tracking-tight group-hover:text-[#D4AF37] transition-colors">
                    {item.q}
                  </span>
                  <span className={`flex-shrink-0 w-9 h-9 border flex items-center justify-center transition-colors ${
                    isOpen ? "border-[#D4AF37] text-[#D4AF37]" : "border-[#2A2A2A] text-[#A0A0A0]"
                  }`}>
                    {isOpen ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                  </span>
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                      className="overflow-hidden"
                    >
                      <p className="pb-8 md:pb-10 pr-12 text-[#A0A0A0] text-base md:text-lg leading-relaxed">
                        {item.a}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </li>
            );
          })}
        </ul>

        <div className="mt-12 pt-8 border-t border-white/10 text-center" data-testid="faq-support-banner">
          <p className="text-xs uppercase tracking-[0.22em] text-[#A0A0A0] mb-3">Still have questions?</p>
          <p className="text-[#F4F4F0] text-base md:text-lg">
            For questions, billing or technical support, contact YBM Studios Support at{" "}
            <a
              href="mailto:support@ybmstudios.com?subject=YBM%20Studios%20Support"
              data-testid="faq-support-email"
              className="text-[#D4AF37] hover:underline font-semibold whitespace-nowrap"
            >
              support@ybmstudios.com
            </a>
          </p>
        </div>
      </div>
    </section>
  );
}
